// Toast notification system.
// Mount <ToastProvider> once in layout.tsx (wrapping body content).
// Call useToast().pushToast({ kind, message }) from anywhere in the tree.
// Toasts auto-dismiss after 4 seconds. Maximum 5 visible at once.

import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ---- Types ---------------------------------------------------------------

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
    id: string;
    kind: ToastKind;
    message: string;
    exiting?: boolean;
}

const EXIT_ANIMATION_MS = 180;

export interface ToastContextValue {
    toasts: Toast[];
    pushToast: (t: Omit<Toast, 'id'>) => void;
    dismissToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

// ---- Provider ------------------------------------------------------------

const AUTO_DISMISS_MS = 4000;
const MAX_TOASTS = 5;

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const dismissToast = useCallback((id: string) => {
        const autoTimer = timersRef.current.get(id);
        if (autoTimer) {
            clearTimeout(autoTimer);
            timersRef.current.delete(id);
        }
        // Mark exiting so the exit animation runs, then remove from state
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, EXIT_ANIMATION_MS);
    }, []);

    const pushToast = useCallback(
        (t: Omit<Toast, 'id'>) => {
            const id = crypto.randomUUID();
            setToasts((prev) => {
                const next = [...prev, { ...t, id }];
                return next.slice(-MAX_TOASTS);
            });
            const timer = setTimeout(() => {
                dismissToast(id);
            }, AUTO_DISMISS_MS);
            timersRef.current.set(id, timer);
        },
        [dismissToast],
    );

    // Cleanup all timers on unmount
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((t) => clearTimeout(t));
        };
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, pushToast, dismissToast }}>
            {children}
            <ToastList toasts={toasts} onDismiss={dismissToast} />
        </ToastContext.Provider>
    );
}

// ---- Rendering -----------------------------------------------------------

function kindStyles(kind: ToastKind): string {
    switch (kind) {
        case 'success':
            return 'border-[rgb(var(--color-positive))]';
        case 'error':
            return 'border-[rgb(var(--color-negative))]';
        case 'info':
            return 'border-[rgb(var(--color-accent))]';
    }
}

function kindDot(kind: ToastKind): string {
    switch (kind) {
        case 'success':
            return 'bg-[rgb(var(--color-positive))]';
        case 'error':
            return 'bg-[rgb(var(--color-negative))]';
        case 'info':
            return 'bg-[rgb(var(--color-accent))]';
    }
}

function ToastList({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
    if (toasts.length === 0) return null;

    return (
        <div
            aria-live="polite"
            aria-label="Notifications"
            className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 w-80"
        >
            {toasts.map((toast) => (
                <div
                    key={toast.id}
                    role="status"
                    className={cn(
                        'flex items-start gap-3 rounded-card p-3 border shadow-md',
                        'bg-[rgb(var(--color-surface))]',
                        'text-[rgb(var(--color-text-primary))]',
                        toast.exiting ? 'animate-toast-out' : 'animate-toast-in',
                        kindStyles(toast.kind),
                    )}
                >
                    <span
                        className={cn(
                            'mt-0.5 h-2 w-2 flex-shrink-0 rounded-full',
                            kindDot(toast.kind),
                        )}
                    />
                    <p className="flex-1 text-sm leading-snug">{toast.message}</p>
                    <button
                        onClick={() => onDismiss(toast.id)}
                        aria-label="Dismiss notification"
                        className="flex-shrink-0 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text-primary))] text-lg leading-none"
                    >
                        ×
                    </button>
                </div>
            ))}
        </div>
    );
}
